import { Button, Typography } from 'antd';
import { Alert02Icon, RefreshIcon } from '@hugeicons/core-free-icons';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useRouteError } from 'react-router-dom';
import { EmptyState } from '@/components/index.js';
import { space, typography } from '@/design/tokens.js';

const { Text } = Typography;

/**
 * A failed dynamic `import()`.
 *
 * The everyday cause is not a bug: the user has an old `index.html` open and
 * the chunk it names was replaced by a deploy. Reloading fixes it and nothing
 * else does, so this case gets its own message — telling somebody to "try
 * again" when only a reload works is advice that never succeeds.
 */
function isChunkLoadFailure(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'ChunkLoadError' ||
    /dynamically imported module|Importing a module script failed|Failed to fetch dynamically/i.test(
      error.message,
    )
  );
}

function describe(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Unknown error';
}

/**
 * The screen behind every unhandled failure.
 *
 * Deliberately plain. It is reached when something has already gone wrong, so
 * it uses no data loading, no API call and nothing that could fail a second
 * time and leave a blank page.
 */
export function ErrorScreen({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const stale = isChunkLoadFailure(error);

  return (
    <div role="alert">
      <EmptyState
        icon={stale ? RefreshIcon : Alert02Icon}
        title={stale ? 'A new version is available' : 'Something went wrong'}
        description={
          stale
            ? 'This page was updated while you had it open. Reload to pick up the new version — nothing you have saved is affected.'
            : 'This screen could not be displayed. Nothing you were working on has been sent.'
        }
        action={{
          label: 'Reload the page',
          onClick: () => window.location.reload(),
          icon: RefreshIcon,
        }}
        secondaryAction={
          stale ? undefined : (
            <Button onClick={onRetry ?? (() => window.location.assign('/'))}>
              {onRetry ? 'Try again' : 'Go to Home'}
            </Button>
          )
        }
      />

      {/*
        The message, never the stack. A stack trace on screen tells the user
        nothing and tells anybody reading over their shoulder the shape of the
        app. It stays available in the console for whoever is debugging.
      */}
      {!stale && (
        <Text
          type="secondary"
          style={{
            display: 'block',
            textAlign: 'center',
            marginTop: space[4],
            fontSize: typography.size.sm,
          }}
        >
          {describe(error)}
        </Text>
      )}
    </div>
  );
}

/**
 * The router's `errorElement`.
 *
 * Catches anything thrown above a screen — the shell, the auth guard, a
 * loader. Without it React Router falls back to its own unstyled page, which
 * in a production build reads "Unexpected Application Error!" over a stack
 * trace.
 */
export function RouteError() {
  const error = useRouteError();

  // Console rather than swallowed: this is the only record of the failure
  // until error reporting exists.
  console.error('Route error', error);

  return <ErrorScreen error={error} />;
}

interface BoundaryProps {
  children: ReactNode;
  /** Changing this resets the boundary — used to clear the error on navigation. */
  resetKey?: string;
}

interface BoundaryState {
  error: unknown;
  resetKey?: string;
}

/**
 * A component-level boundary, used by `Screen` around every page.
 *
 * Separate from the router's `errorElement` on purpose: this one keeps the
 * sidebar and top bar on screen, so a page that fails leaves the user able to
 * navigate somewhere else instead of stranding them on a full-window error.
 */
export class ErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): Partial<BoundaryState> {
    return { error };
  }

  /** Clears the error when the route changes, so navigating away recovers. */
  static getDerivedStateFromProps(
    props: BoundaryProps,
    state: BoundaryState,
  ): BoundaryState | null {
    if (state.resetKey !== props.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Screen error', error, info.componentStack);
  }

  override render() {
    if (this.state.error) {
      return (
        <ErrorScreen error={this.state.error} onRetry={() => this.setState({ error: null })} />
      );
    }
    return this.props.children;
  }
}
